import React, { useEffect } from "react";

import {
  Beneficiary1Name,
  Beneficiary1Relationship,
  Beneficiary1Percentage,
  Beneficiary1BirthDate,
  Beneficiary1SocialSecurityNumber,
  Beneficiary1PhoneNumber,
  Beneficiary1Address,
  Beneficiary1City,
  Beneficiary1State,
  Beneficiary1ZipCode,
  Beneficiary2Type,
  Beneficiary2Name,
  Beneficiary2Relationship,
  Beneficiary2BirthDate,
  Beneficiary2SocialSecurityNumber,
  Beneficiary2PhoneNumber,
  Beneficiary2Address,
  Beneficiary2City,
  Beneficiary2State,
  Beneficiary2ZipCode,
  useGetBeneficiaryPairQuery,
  useUpdateBeneficiaryPairMutation,
} from "Features/BeneficiaryPairs";
import createDecorator from "final-form-focus";
import { Form, useForm, useFormState } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import commaListify from "Utils/commaListify";
import { validateRequired, validateDate, validateBeforeToday, validateNoEmoji, composeValidators } from "Utils/Validations";
import BasicLoading from "ZeroRuby/BasicLoading";

import Collapse from "Cobalt/Collapse";
import { SurfaceHeader, SurfaceSection } from "Cobalt/Surface";
import AgentHeader from "Common/Agent/AgentHeader";
import BodyText from "Common/BodyText";
import { SIFinalFormField, SIFormSelect, SIFormRadioGroup } from "Common/forms";
import { dateMask } from "Common/forms/masks";
import { formFormatDate, formParseDate } from "Common/forms/utils";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import { usePartnerContext } from "Common/PartnerContext";

import { stepCompleted } from "./actions";
import { useUpdateEmployeePolicyMutation, getEmployeeForm, getEmployeeApplication, useGetApplicationQuery, getCompanyProfileID } from "./api";
import { DECLINATION_REASONS } from "./constants";
import FooterNavigation from "./FooterNavigation";
import Page from "./Page";

import type { BeneficiaryPair, EmployeeForm } from "Utils/types";

const focusOnErrors = createDecorator();
const decorators = [focusOnErrors];

const formFormatEmployeeForm = (employee: EmployeeForm | null | undefined) =>
  employee
    ? {
        employeeType: employee.employeeType,
        jobTitle: employee.jobTitle,
        hiredAt: employee.hiredAt ? formFormatDate(new Date(employee.hiredAt)) : null,
        salaryAmount: employee.salaryAmount,
      }
    : {};

const formParseEmployeeForm = (values: any) => ({
  ...values,
  hiredAt: formParseDate(values.hiredAt),
});

const formFormatBeneficiaryPair = (beneficiaryPair: BeneficiaryPair) => ({
  ...beneficiaryPair,
  beneficiary1Percentage: beneficiaryPair.beneficiary2Type === "primary" ? beneficiaryPair.beneficiary1Percentage : 50,
  beneficiary2Percentage: beneficiaryPair.beneficiary2Type === "primary" ? beneficiaryPair.beneficiary2Percentage : 50,
});

const DeclinationForm = ({ declinationReason }) => {
  const selectedReason = DECLINATION_REASONS.find(({ value }) => value === declinationReason);
  const requiredAdditional = selectedReason ? selectedReason.requiredAdditional : [];

  return (
    <SurfaceSection title="Declination Information">
      <Row>
        <SIFinalFormField
          name="employeePolicy.declinationReason"
          label="Please select one of the reasons for declining that applies to you"
          component={SIFormSelect}
          componentProps={{
            options: DECLINATION_REASONS.map(({ value, label }) => [value, label]),
          }}
          validate={validateRequired}
          guidance="The insurance carrier asks for a reason for declining coverage. Answering this question helps speed up the application process."
          md={6}
        />
      </Row>
      <Row style={{ minHeight: 88 }}>
        {requiredAdditional.indexOf("declinationReason1CarrierName") !== -1 && (
          <SIFinalFormField name="employeePolicy.declinationReason1CarrierName" label="Carrier Name" validate={validateRequired} md={3} />
        )}

        {requiredAdditional.indexOf("declinationReason1IDNumber") !== -1 && (
          <SIFinalFormField name="employeePolicy.declinationReason1IDNumber" label="ID Number" validate={validateRequired} md={3} />
        )}

        {requiredAdditional.indexOf("declinationReason4Explanation") !== -1 && (
          <SIFinalFormField
            name="employeePolicy.declinationReason4Explanation"
            label="Explanation"
            validate={composeValidators(validateRequired, validateNoEmoji)}
            md={3}
          />
        )}
      </Row>
    </SurfaceSection>
  );
};

const BeneficiaryPairSection = () => {
  const { values } = useFormState();
  const { change } = useForm();

  const { beneficiaryPair: { beneficiary1Percentage, beneficiary2Percentage, beneficiary2Type, beneficiary1Name, beneficiary2Name } = {} } = values;
  useEffect(() => {
    const b1Percentage = parseInt(beneficiary1Percentage, 10);
    if (beneficiary2Type === "primary") {
      change("beneficiaryPair.beneficiary2Percentage", 100 - b1Percentage);
    }
  }, [beneficiary1Percentage, beneficiary2Type, change]);
  const employeePolicy = useSelector(getEmployeeApplication);
  const companyProfileID = useSelector(getCompanyProfileID);
  // Do not require SSN if company is Oh So Original
  // because many of the beneficiaries are international
  // and do not have SSNs
  const OH_SO_ORIGINAL_COMPANY_PROFILE = 584156;
  const requireSSN = Number(companyProfileID) !== OH_SO_ORIGINAL_COMPANY_PROFILE;
  const { insurancesWithBeneficiariesCategories } = employeePolicy;
  const insurancesText =
    insurancesWithBeneficiariesCategories.length > 1
      ? `${commaListify(insurancesWithBeneficiariesCategories)} insurances require`
      : `${insurancesWithBeneficiariesCategories[0]} insurance requires`;

  return (
    <SurfaceSection title="Beneficiaries">
      <Row>
        <Col>
          <BodyText mb={3}>Your {insurancesText} you to set a beneficiary. In the event of death, your beneficiary will receive the benefit.</BodyText>
        </Col>
      </Row>
      <Row>
        <Col>
          <BodyText mb={3} weight="bold">
            Primary Beneficiary
          </BodyText>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Beneficiary1Name inputNamePrefix="beneficiaryPair" noBounce />
        </Col>
        <Col md={6}>
          <Beneficiary1BirthDate inputNamePrefix="beneficiaryPair" noBounce />
        </Col>
        <Col md={6}>
          <Beneficiary1Address inputNamePrefix="beneficiaryPair" noBounce />
        </Col>
        <Col md={6}>
          <Beneficiary1City inputNamePrefix="beneficiaryPair" noBounce />
        </Col>
        <Col md={6}>
          <Beneficiary1State inputNamePrefix="beneficiaryPair" noBounce />
        </Col>
        <Col md={6}>
          <Beneficiary1ZipCode inputNamePrefix="beneficiaryPair" noBounce />
        </Col>
        <Col md={6}>
          <Beneficiary1PhoneNumber inputNamePrefix="beneficiaryPair" noBounce />
        </Col>
        <Col md={6}>
          <Beneficiary1Relationship inputNamePrefix="beneficiaryPair" noBounce />
        </Col>
        <Col md={6}>
          <Beneficiary1SocialSecurityNumber inputNamePrefix="beneficiaryPair" required={requireSSN} noBounce />
        </Col>
      </Row>
      <Row>
        <Col>
          <BodyText mb={3} weight="bold">
            Add a Second Beneficiary (Optional)
          </BodyText>
        </Col>
      </Row>
      <Row>
        <Col>
          <Beneficiary2Type inputNamePrefix="beneficiaryPair" noBounce />
        </Col>
      </Row>
      <Collapse in={["primary", "contingent"].includes(beneficiary2Type)} mountOnEnter unmountOnExit>
        <Div pb={3}>
          <Row>
            <Col md={6}>
              <Beneficiary2Name inputNamePrefix="beneficiaryPair" noBounce />
            </Col>
            <Col md={6}>
              <Beneficiary2BirthDate inputNamePrefix="beneficiaryPair" noBounce />
            </Col>
            <Col md={6}>
              <Beneficiary2Address inputNamePrefix="beneficiaryPair" noBounce />
            </Col>
            <Col md={6}>
              <Beneficiary2City inputNamePrefix="beneficiaryPair" noBounce />
            </Col>
            <Col md={6}>
              <Beneficiary2State inputNamePrefix="beneficiaryPair" noBounce />
            </Col>
            <Col md={6}>
              <Beneficiary2ZipCode inputNamePrefix="beneficiaryPair" noBounce />
            </Col>
            <Col md={6}>
              <Beneficiary2PhoneNumber inputNamePrefix="beneficiaryPair" noBounce />
            </Col>
            <Col md={6}>
              <Beneficiary2Relationship inputNamePrefix="beneficiaryPair" noBounce />
            </Col>
            <Col md={6}>
              <Beneficiary2SocialSecurityNumber inputNamePrefix="beneficiaryPair" required={requireSSN} noBounce />
            </Col>
          </Row>
          {beneficiary2Type === "primary" && (
            <>
              <Row>
                <Col>
                  <BodyText mb={3} weight="bold">
                    Assign Benefit Amounts
                  </BodyText>
                </Col>
              </Row>
              <Row>
                <Col>
                  <BodyText mb={3}>
                    When you have two primary beneficiaries the benefit is split between them. Choose the percentage each beneficiary will receive.
                  </BodyText>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Beneficiary1Percentage inputNamePrefix="beneficiaryPair" noBounce label={`${beneficiary1Name || "Beneficiary 1"}`} />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  {beneficiary2Name || "Beneficiary 2"}
                  <br />
                  {Number.isNaN(parseInt(beneficiary2Percentage, 10)) ? "" : `${beneficiary2Percentage}%`}
                </Col>
              </Row>
            </>
          )}
        </Div>
      </Collapse>
    </SurfaceSection>
  );
};

const AdditionalInformationForm = ({ backLink, onClickBack }) => {
  const { syncedEmployeeFields } = usePartnerContext();
  const [updateEmployeeApplication] = useUpdateEmployeePolicyMutation();
  const dispatch = useDispatch();
  const employeePolicy = useSelector(getEmployeeApplication);
  const { isBeneficiariesRequired } = employeePolicy || {};
  const employeeForm = useSelector(getEmployeeForm);
  const employeeFormID = employeeForm?.id;
  const { data: { beneficiaryPair } = { beneficiaryPair: {} } } = useGetBeneficiaryPairQuery({ employeeFormID });
  const [updateBeneficiaryPair] = useUpdateBeneficiaryPairMutation();
  const employeeHasHiredAt = employeeForm.hiredAt;
  const employeeHasJobTitle = employeeForm.jobTitle;
  const employeeHasType = employeeForm.employeeType;
  const isDeclination = employeePolicy && employeePolicy.isEnrolling === "No";
  const initialValues = {
    employeePolicy,
    employeeForm: formFormatEmployeeForm(employeeForm),
    beneficiaryPair: formFormatBeneficiaryPair(beneficiaryPair),
  };

  const onSubmit = async ({ employeePolicy: employeePolicyValues, employeeForm: employeeFormValues, beneficiaryPair: beneficiaryPairValues }) => {
    const updateEmployeeApplicationRes = await updateEmployeeApplication({
      ...employeePolicyValues,
      employeeForm: formParseEmployeeForm(employeeFormValues),
    });
    if (updateEmployeeApplicationRes.error) {
      const { errors } = updateEmployeeApplicationRes.error.data;
      return errors;
    }
    if (isBeneficiariesRequired) {
      const updateBeneficiaryPairRes = await updateBeneficiaryPair({
        employeeFormID,
        beneficiaryPair: beneficiaryPairValues,
      });
      if (updateBeneficiaryPairRes.error) {
        const { errors } = updateBeneficiaryPairRes.error.data;
        return errors;
      }
    }
    dispatch(stepCompleted("additionalInformation"));
    return true;
  };

  return (
    <Form onSubmit={onSubmit} decorators={decorators} initialValues={initialValues} keepDirtyOnReinitialize>
      {({ handleSubmit, submitting, values }) => {
        const { declinationReason } = values.employeePolicy;
        return (
          <form onSubmit={handleSubmit}>
            <SurfaceHeader>{isDeclination ? "Additional Information" : "Employment Information"}</SurfaceHeader>
            <SurfaceSection>
              <AgentHeader>
                {isDeclination
                  ? "We just need a bit of information about why you are declining coverage and some employment details"
                  : "We just need to finalize some employment details"}
              </AgentHeader>
            </SurfaceSection>
            {isDeclination && <DeclinationForm declinationReason={declinationReason} />}
            <SurfaceSection title={isDeclination ? "Employment Information" : null}>
              {!(syncedEmployeeFields.jobTitle && employeeHasJobTitle) && (
                <Row>
                  <SIFinalFormField name="employeeForm.jobTitle" label="Job Title" validate={validateRequired} md={3} />
                </Row>
              )}

              {!(syncedEmployeeFields.hiredAt && employeeHasHiredAt) && (
                <Row>
                  <SIFinalFormField
                    name="employeeForm.hiredAt"
                    label="Date of Hire"
                    hint="MM/DD/YYYY"
                    componentProps={{ mask: dateMask }}
                    validate={composeValidators(validateRequired, validateDate, validateBeforeToday)}
                    md={3}
                  />
                </Row>
              )}

              {!employeeHasType && (
                <Row>
                  <SIFinalFormField
                    name="employeeForm.employeeType"
                    label="Employee Type"
                    component={SIFormRadioGroup}
                    componentProps={{
                      options: [
                        ["full_time", "Payroll Employee (W-2)"],
                        ["owner", "Owner, not on Payroll"],
                        ["1099", "1099 Contractor"],
                      ],
                    }}
                    type="radio"
                    validate={validateRequired}
                  />
                </Row>
              )}

              {!isDeclination &&
                syncedEmployeeFields.jobTitle &&
                employeeHasJobTitle &&
                syncedEmployeeFields.hiredAt &&
                employeeHasHiredAt &&
                employeeHasType && (
                  <Row>
                    <Col>No additional information required!</Col>
                  </Row>
                )}
            </SurfaceSection>
            {isBeneficiariesRequired && <BeneficiaryPairSection employeeForm={employeeForm} />}
            <FooterNavigation backLink={backLink} onClickBack={onClickBack} submitting={submitting} />
          </form>
        );
      }}
    </Form>
  );
};

type Props = {
  backLink: string;
  readonly onClickBack: () => void;
};

export const AdditionalInformationPage = ({ backLink, onClickBack }: Props) => {
  const { isLoading } = useGetApplicationQuery();
  const employeePolicy = useSelector(getEmployeeApplication);
  const { isBeneficiariesRequired } = employeePolicy || {};
  const employeeForm = useSelector(getEmployeeForm);
  const employeeFormID = employeeForm?.id;
  const { isLoading: isLoadingBeneficiaryPair } = useGetBeneficiaryPairQuery({ employeeFormID }, { skip: !employeeFormID || !isBeneficiariesRequired });
  return (
    <Page title="Additional Information">
      {isLoading || (isBeneficiariesRequired && isLoadingBeneficiaryPair) ? (
        <BasicLoading />
      ) : (
        <AdditionalInformationForm backLink={backLink} onClickBack={onClickBack} />
      )}
    </Page>
  );
};

export default AdditionalInformationPage;
